import { useState } from "react";
import { Truck, Check, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { updateOrderTracking } from "@/lib/api";

export default function OrderTrackingForm({ order, onSaved }) {
  const [carrier, setCarrier] = useState(order.tracking_carrier || "");
  const [number, setNumber] = useState(order.tracking_number || "");
  const [url, setUrl] = useState(order.tracking_url || "");
  const [busy, setBusy] = useState(false);

  const hasExisting = !!(order.tracking_number || order.tracking_url);

  const save = async () => {
    if (!number.trim() && !url.trim()) {
      toast.error("Enter a tracking number or a tracking link.");
      return;
    }
    setBusy(true);
    try {
      const updated = await updateOrderTracking(order.id, {
        carrier: carrier.trim(),
        number: number.trim(),
        url: url.trim(),
      });
      toast.success("Tracking saved — customer emailed.");
      onSaved?.(updated);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save tracking");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid={`order-tracking-${order.id}`}
      className="rounded-xl border border-border p-4 bg-background"
    >
      <div className="flex items-center gap-2 mb-3">
        <Truck size={15} strokeWidth={1.6} className="text-primary" />
        <p className="text-sm font-medium">Shipping &amp; tracking</p>
        {hasExisting && (
          <span className="text-[10px] uppercase tracking-[0.12em] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5">
            Sent
          </span>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <input
          data-testid={`tracking-carrier-${order.id}`}
          type="text"
          placeholder="Carrier (e.g. Canada Post)"
          value={carrier}
          onChange={(e) => setCarrier(e.target.value)}
          className="px-4 py-2.5 rounded-full border border-border bg-transparent focus:outline-none focus:border-primary text-sm"
        />
        <input
          data-testid={`tracking-number-${order.id}`}
          type="text"
          placeholder="Tracking number"
          value={number}
          onChange={(e) => setNumber(e.target.value)}
          className="px-4 py-2.5 rounded-full border border-border bg-transparent focus:outline-none focus:border-primary text-sm font-mono"
        />
        <input
          data-testid={`tracking-url-${order.id}`}
          type="url"
          placeholder="Tracking link (optional)"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          className="px-4 py-2.5 rounded-full border border-border bg-transparent focus:outline-none focus:border-primary text-sm"
        />
      </div>
      <div className="flex items-center gap-3 mt-3">
        <button
          type="button"
          data-testid={`tracking-save-${order.id}`}
          onClick={save}
          disabled={busy}
          className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-primary text-primary-foreground text-sm hover:-translate-y-0.5 transition-transform shadow-soft disabled:opacity-60"
        >
          <Check size={14} strokeWidth={2} />
          {busy ? "Saving…" : hasExisting ? "Update & re-email customer" : "Save & email customer"}
        </button>
        {(order.tracking_url || order.tracking_number) && (
          <a
            href={order.tracking_url || `https://www.google.com/search?q=${encodeURIComponent(order.tracking_number)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
          >
            View current tracking <ExternalLink size={12} strokeWidth={1.5} />
          </a>
        )}
      </div>
      <p className="text-[11px] text-muted-foreground mt-2">
        Adding or updating tracking emails the buyer at {order.email || "their account email"}.
      </p>
    </div>
  );
}
